import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/es";
import "dayjs/locale/pt";
import "dayjs/locale/en-gb";
import Decimal from "decimal.js";
import axios from "axios";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShipping from '@mui/icons-material/LocalShipping';
import EditPage from "./EditPage";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import {
  fetchForInput,
  fetchProducts,
  validateDecimal,
  formatCurrency,
  getDescription,
  getSubtotal,
  validateNonEmpty,
  handleError,
  resolveDateFormattingLocale,
  bcp47ToDayjsLocale,
  getDayjsDateTimeFormatForLocale,
  getDayjsDateFormatForLocale,
  resolveHour12Preference,
} from "../../utils/lib";
import { useTranslation } from 'react-i18next';
import { useConfig } from "../../providers/ConfigProvider";
import usePurchaseStore from "../../store/purchaseStore";
import { useAppBar } from '../../providers/AppBarProvider';
import ListItemWAvatarRemove from "../../layouts/ListItemWAvatarRemove";
import ListItemProductRemove from "../../layouts/ListItemProductRemove";
import { useSnackbar } from "../../providers/SnackBarProvider";
import ProductUnitSelector from "../../layouts/ProductUnitSelector";
import useNavigationHelperStore from "../../store/navigationHelperStore";

const EditPurchase = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const snackbar = useSnackbar();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [deposits, setDeposits] = useState([]);
  const [paymentsTypes, setPaymentsTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const {updateTitle, toggleFab, toggleCloseIcon} = useAppBar();
  const { config, isLoadingConfig } = useConfig();

  const dateFormattingLocale = useMemo(() => resolveDateFormattingLocale(config), [config]);
  const useHour12 = useMemo(
    () => resolveHour12Preference(config, dateFormattingLocale),
    [config, dateFormattingLocale]
  );
  const dateTimePickerFormat = useMemo(
    () => getDayjsDateTimeFormatForLocale(dateFormattingLocale, useHour12),
    [dateFormattingLocale, useHour12]
  );
  const dateDisplayFormat = useMemo(
    () => getDayjsDateFormatForLocale(dateFormattingLocale),
    [dateFormattingLocale]
  );
  const dayjsPickerLocale = useMemo(
    () => bcp47ToDayjsLocale(dateFormattingLocale),
    [dateFormattingLocale]
  );

  // Usar el store de Zustand
  const {
    purchase,
    purchaseItem,
    paymentItem,
    setPurchaseItem,
    setPaymentItem,
    updatePurchase,
    addItem,
    removeItem,
    addPayment,
    removePayment,
    resetPurchase,
    resetPurchaseItem,
    resetPaymentItem
  } = usePurchaseStore();
  
  const { from, state, resetNavigation } = useNavigationHelperStore();

  // Referencias para los campos de formulario
  const [errors, setErrors] = useState({});
  const refs = {
    receipt: useRef(),
    product: useRef(),
    quantity: useRef(),
    price: useRef(),
    discount: useRef(),
    amount: useRef(),
    deposit: useRef(),
    payment: useRef(),
  };

  // Cargar datos iniciales
  useEffect(() => {
    updateTitle('', t('edit.purchaseTitle'), t('common.completeData'));
    toggleFab(false);
    toggleCloseIcon(true);

    if (location.state?.provider) {
      updatePurchase({
        provider: location.state.provider
      });
    }

    if (location.state?.product) manageInsertPurchaseItem(location.state.product);

    const fetchData = async () => {
      const purchasesForInput = await fetchForInput("purchases", "purchases_for_input");
      setDeposits(purchasesForInput.deposits);
      setPaymentsTypes(purchasesForInput.finance_types_payments);
      const products = await fetchProducts();
      setProducts(products);

      setIsLoading(false);

      setTimeout(() => {
        if (refs.product.current && !location.state?.product) {
          refs.product.current.focus();
        }
      }, 100);
    };
    fetchData();
  }, []);

  // Establecer depósito predeterminado
  useEffect(() => {
    setDefaultDeposit();
  }, [deposits]);

  const setDefaultDeposit = () => {
    if (deposits.length > 0 && !purchase.deposit) {
      updatePurchase({ deposit: deposits[0] });
    }
  };

  // Funciones de búsqueda y remoción de proveedor
  const handleSearchProvider = () => {
    navigate("/main/search-provider", { state: { from: "/main/edit-purchase" } });
  };
  
  const handleRemoveProvider = () => {
    updatePurchase({ provider: null });
  };

  const handleSearchProduct = () => {
    navigate("/main/search-product", { state: { from: "/main/edit-purchase" } });
  };
  
  // Agregar producto: valida campos, agrega el producto y recalcula total y deuda
  const handleAddProduct = () => {
    if (!purchaseItem.product || !purchaseItem.quantity || !purchaseItem.price) {
      snackbar(t('edit.selectProduct'));
      return;
    }

    if (
      !validateDecimal("quantity", purchaseItem.quantity, setErrors, refs.quantity) ||
      !validateDecimal("price", purchaseItem.price, setErrors, refs.price) ||
      (purchaseItem.discount_surcharge &&
        !validateDecimal("discount", purchaseItem.discount_surcharge, setErrors, refs.discount))
    )
      return;

    addItem(structuredClone(purchaseItem));
    const { total, debt } = calculateTotalAndDebt([...purchase.items, purchaseItem], purchase.payments);
    updatePurchase({ total, debt });
    resetPurchaseItem();

    refs.product.current.focus();
  };
  
  // Remover producto y actualizar total y deuda
  const handleRemoveProduct = (index) => {
    removeItem(index);
    const updatedItems = purchase.items.filter((_, i) => i !== index);
    const { total, debt } = calculateTotalAndDebt(updatedItems, purchase.payments);
    updatePurchase({ total, debt });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (deposits.length > 1 && !validateNonEmpty("deposit", purchase.deposit?.name, setErrors, refs.deposit)) {
      return;
    }

    if (!purchase.items || purchase.items.length === 0) {
      snackbar(t('edit.selectProduct'));
      return;
    }

    // Make sure debt is set to total initially
    if (!purchase.payments || purchase.payments.length === 0) {
      updatePurchase({ debt: purchase.total });
    }

    // Open payment dialog instead of showing confirmation
    setPaymentDialogOpen(true);
    
    // Set default payment method to "Efectivo" if available
    const efectivoPayment = paymentsTypes.find(payment => payment.name.toLowerCase() === "efectivo");
    if (efectivoPayment) {
      setPaymentItem({
        finance_types_payment: efectivoPayment,
        amount: purchase.total ? purchase.total.toString() : "",
        date: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        id: efectivoPayment.id,
        name: efectivoPayment.name,
      });
    } else if (paymentsTypes.length > 0) {
      // If "Efectivo" not found, use the first payment method
      const firstPayment = paymentsTypes[0];
      setPaymentItem({
        finance_types_payment: firstPayment,
        amount: purchase.total ? purchase.total.toString() : "",
        date: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        id: firstPayment.id,
        name: firstPayment.name,
      });
    }
    
    // Set focus to amount field after the dialog opens
    setTimeout(() => {
      if (refs.amount.current) {
        refs.amount.current.focus();
        refs.amount.current.select();
      }
    }, 100);
  };

  const handleAddPayment = () => {
    if (currentPaymentIsZero()) {
      snackbar(t('edit.enterPaymentAmount'));
      return;
    }

    if (!paymentItem.finance_types_payment) {
      snackbar(t('edit.selectPaymentMethod'));
      return;
    }

    if (!validateDecimal("amount", paymentItem.amount, setErrors, refs.amount))
      return;

    const currentPayment = getCurrentPayment();
    
    if (!isPossiblePayment(currentPayment)) {
      snackbar(t('edit.paymentExceedsTotal'));
      return;
    }

    addPayment(structuredClone(currentPayment));
    const { total, debt } = calculateTotalAndDebt(purchase.items, [...purchase.payments, currentPayment]);
    updatePurchase({ total, debt });

    // Set default payment for next entry
    const efectivoPayment = paymentsTypes.find(payment => payment.name.toLowerCase() === "efectivo");
    if (efectivoPayment) {
      setPaymentItem({
        finance_types_payment: efectivoPayment,
        amount: debt > 0 ? debt.toString() : "",
        date: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        id: efectivoPayment.id,
        name: efectivoPayment.name,
      });
    } else {
      setPaymentItem({
        finance_types_payment: null,
        amount: "",
      });
    }
  };

  // Remover pago y actualizar la deuda
  const handleRemovePayment = (index) => {
    removePayment(index);
    const updatedPayments = purchase.payments.filter((_, i) => i !== index);
    const { total, debt } = calculateTotalAndDebt(purchase.items, updatedPayments);
    updatePurchase({ total, debt });

    // Set default payment for next entry if no payments remain
    if (updatedPayments.length === 0) {
      const efectivoPayment = paymentsTypes.find(payment => payment.name.toLowerCase() === "efectivo");
      if (efectivoPayment) {
        setPaymentItem({
          finance_types_payment: efectivoPayment,
          amount: debt > 0 ? debt.toString() : "",
          date: dayjs().format('YYYY-MM-DD HH:mm:ss'),
          id: efectivoPayment.id,
          name: efectivoPayment.name,
        });
      } else {
        setPaymentItem({
          finance_types_payment: null,
          amount: "",
        });
      }
    }
  };

  const handleConfirmSave = () => {
    setConfirmDialogOpen(false);
    save();
  };

  const handlePaymentDialogClose = () => {
    setPaymentDialogOpen(false);
    resetPaymentItem();
  };

  const handlePaymentDialogSave = () => {
    if (!validateNullProviderAndTotalPay()) {
      snackbar(t('edit.selectProviderForDebt'));
      return;
    }

    let currentPayment = null;
    if (hasNoPayments() && !currentPaymentIsZero()) {
      if (currentPaymentIsGreaterThanTotalToPay()) {
        currentPayment = getCurrentPaymentMax();
      } else {
        currentPayment = getCurrentPayment();
      }
    }

    if (currentPayment) {
      addPayment(structuredClone(currentPayment));
    }

    setPaymentDialogOpen(false);
    setReviewDialogOpen(true);
  };

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);

    // Handle navigation after closing success dialog
    if (from != "/main" && from && state) {
      navigate(from, state);
      //resetNavigation();
    } else {
      if (refs.product.current) {
        setTimeout(() => {
          refs.product.current.focus();
        }, 100);
      }
    }
  };

  // Review dialog functions
  const handleReviewDialogClose = () => {
    setReviewDialogOpen(false);
  };

  const handleReviewDialogSave = () => {
    setReviewDialogOpen(false);
    save();
  };

  const save = async () => {
    setIsLoading(true);
    try {
      // Format the date to MySQL datetime format before sending
      const purchaseToSend = {
        ...purchase,
        date: dayjs(purchase.date).format('YYYY-MM-DD HH:mm:ss')
      };
      
      const response = await axios.post(`${import.meta.env.VITE_API_URL}purchases`, 
        purchaseToSend, 
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
        }
      });

      resetPurchase();
      resetPaymentItem();
      setDefaultDeposit();

      // Show success dialog instead of snackbar
      setSuccessDialogOpen(true);

    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  // Calcula el total de la compra y la deuda (total - pagos realizados)
  const calculateTotalAndDebt = (items, payments = []) => {
    let total = new Decimal(0);
    items.forEach((item) => {
      const quantity = new Decimal(item.quantity.toString().replace(",", ".") || 0);
      const price = new Decimal(item.price.toString().replace(",", ".") || 0);
      const discount = new Decimal(item.discount_surcharge.toString().replace(",", ".") || 0);
      const subtotal = quantity.times(price);
      const discountAmount = subtotal.times(discount.div(100));
      total = total.plus(subtotal.minus(discountAmount));
    });
    total = total.toDecimalPlaces(2);

    const totalPayments = payments.reduce(
      (sum, payment) =>
        sum.plus(new Decimal(payment.amount.toString().replace(",", ".") || 0)),
      new Decimal(0)
    );
    const debt = total.minus(totalPayments).toDecimalPlaces(2);
    return { total: total.toNumber(), debt: debt.toNumber() };
  };

  // Modify manageInsertPurchaseItem to handle default unit assignment
  const manageInsertPurchaseItem = (product) => {
    if (product) {
      setPurchaseItem({
        product: product,
        quantity: (1).toFixed(2),
        price: product.cost ? product.cost.toFixed(2) : (0).toFixed(2),
        discount_surcharge: (0).toFixed(2),
        selected_unit: product.unit.first_unit_string,
      });

      setTimeout(() => {
        if (refs.quantity.current) {
          refs.quantity.current.focus();
          refs.quantity.current.select();
        }
      }, 100);
      
    } else {
      resetPurchaseItem();
    }
  };

  // Add new helper functions for payment logic
  const getSumOfPayments = () => {
    return purchase.payments.reduce(
      (sum, payment) => sum.plus(new Decimal(payment.amount.toString().replace(",", "."))),
      new Decimal(0)
    );
  };

  const getCurrentPaymentAmount = () => {
    if (!paymentItem?.amount || paymentItem.amount === "") return new Decimal(0);
    return new Decimal(paymentItem.amount.toString().replace(",", "."));
  };

  const getCurrentPayment = () => {
    return {
      id: paymentItem.finance_types_payment.id,
      name: paymentItem.finance_types_payment.name,
      amount: getCurrentPaymentAmount().toNumber(),
      finance_types_payment: paymentItem.finance_types_payment,
      date: dayjs().format('YYYY-MM-DD'),
    };
  };

  const getCurrentPaymentMax = () => {
    const payment = getCurrentPayment();
    const currentTotal = new Decimal(purchase.total);
    const sumOfPayments = getSumOfPayments();
    const remainingAmount = currentTotal.minus(sumOfPayments);
    payment.amount = remainingAmount.toNumber();
    return payment;
  };

  const currentPaymentIsGreaterThanTotalToPay = () => {
    const currentTotal = new Decimal(purchase.total);
    const sumOfPayments = getSumOfPayments();
    const totalWithCurrentPayment = sumOfPayments.plus(getCurrentPaymentAmount());
    return totalWithCurrentPayment.greaterThan(currentTotal);
  };

  const currentPaymentIsZero = () => {
    return getCurrentPaymentAmount().equals(0);
  };

  const hasNoPayments = () => {
    return !purchase.payments || purchase.payments.length === 0;
  };

  const hasDebtBalance = () => {
    const currentTotal = new Decimal(purchase.total);
    let sumOfPayments = getSumOfPayments();
    
    if (hasNoPayments()) {
      sumOfPayments = sumOfPayments.plus(getCurrentPaymentAmount());
    }
    
    return sumOfPayments.lessThan(currentTotal);
  };

  const validateNullProviderAndTotalPay = () => {
    if (!purchase.provider && hasDebtBalance()) {
      return false;
    }
    return true;
  };

  const setBalanceInViews = () => {
    const currentPaymentAmount = getCurrentPaymentAmount();
    let sumOfPayments = getSumOfPayments();

    if (hasNoPayments()) {
      sumOfPayments = sumOfPayments.plus(currentPaymentAmount);
    }

    const total = new Decimal(purchase.total || 0);
    const balance = sumOfPayments.minus(total);
    const zero = new Decimal(0);

    return {
      balance: balance.toNumber(),
      isDebt: balance.lessThan(zero),
      isChange: balance.greaterThan(zero),
      isPerfect: balance.equals(zero)
    };
  };

  const isPossiblePayment = (payment) => {
    const paymentAmount = new Decimal(payment.amount || 0);
    const currentTotal = new Decimal(purchase.total || 0);
    const sumOfPayments = getSumOfPayments();
    const totalWithNewPayment = sumOfPayments.plus(paymentAmount);
    
    return totalWithNewPayment.lessThanOrEqualTo(currentTotal);
  };

  const calculateBalance = () => {
    const totalPayments = purchase.payments?.reduce(
      (sum, payment) => sum.plus(new Decimal(payment.amount || 0)),
      new Decimal(0)
    ) || new Decimal(0);
    
    const total = new Decimal(purchase.total || 0);
    return total.minus(totalPayments);
  };

  return (
    <EditPage
      onSubmit={handleSubmit}
      isLoading={isLoading || isLoadingConfig}
    >
      <Box sx={{ width: "100%", marginTop: 1 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
          <DateTimePicker
            label={t('common.dateTime')}
            value={purchase.date}
            onChange={(newValue) => updatePurchase({ date: newValue })}
            renderInput={(params) => <TextField {...params} fullWidth />}
            slotProps={{ textField: { fullWidth: true } }}
            format={dateTimePickerFormat}
            ampm={useHour12}
          />
        </LocalizationProvider>
      </Box>

      <TextField
        fullWidth
        margin="normal"
        label={t('edit.receiptLabel')}
        name="receipt"
        value={purchase.receipt}
        onChange={(e) => updatePurchase({ receipt: e.target.value })}
        inputRef={refs.receipt}
      />

      <Box display="flex" gap={2} alignItems="center">
        <ListItemWAvatarRemove
          primary={purchase.provider?.name || t('edit.selectProvider')}
          onClick={handleSearchProvider}
          onDelete={handleRemoveProvider}
          avatarContent={<LocalShipping />}
        />
      </Box>

      {deposits.length > 1 && (
        <Box
          display="flex"
          gap={2}
          width="100%"
          justifyContent="center"
          sx={{ marginTop: 3 }}
        >
          <Box flex={1}>
            <Autocomplete
              options={deposits}
              getOptionLabel={(option) => option.name}
              value={purchase.deposit}
              onChange={(_, newValue) => updatePurchase({ deposit: newValue })}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label={t('common.deposit')} 
                  fullWidth 
                  inputRef={refs.deposit}
                  error={!!errors.deposit}
                  helperText={errors.deposit}
                />
              )}
              freeSolo={false}
            />
          </Box>
        </Box>
      )}

      <Divider sx={{ marginY: 2 }} />

      <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
        <Typography variant="h6" fontWeight="bold">
          Total: {formatCurrency(purchase.total)}
        </Typography>
      </Box>

      <Divider sx={{ marginTop: 2, marginBottom: 1 }} />

      <Box display="flex" gap={1} alignItems="center">
        {/* Product autocomplete with search functionality */}
        <Autocomplete
          fullWidth
          options={products}
          getOptionLabel={(option) => (option ? option.name : "")}
          value={purchaseItem?.product}
          onChange={(e, newValue) => {
            manageInsertPurchaseItem(newValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('common.productService')}
              name="product"
              margin="normal"
              inputRef={refs.product}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  setTimeout(() => {
                    if (!refs.product.current?.value) {
                      handleSubmit(e);
                    }
                  }, 100);
                }
              }}
            />
          )}
        />

        {/* Product search button */}
        <IconButton onClick={handleSearchProduct} sx={{ marginTop: 1 }}>
          <SearchIcon />
        </IconButton>

        {/* Quantity input field */}
        <TextField
          label={t('common.quantity')}
          type="text"
          inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
          value={purchaseItem?.quantity}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9.,]/g, "");
            setPurchaseItem({ ...purchaseItem, quantity: value });
          }}
          sx={{ marginTop: 1, "& .MuiInputBase-input": { textAlign: "right" } }}
          inputRef={refs.quantity}
          error={!!errors.quantity}
          helperText={errors.quantity}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              refs.price.current.select();
              refs.price.current.focus();
            }
          }}
        />

        {/* Unit selector - Only displayed for products with complex units */}
        {purchaseItem.product?.unit.is_complex === 1 && (
          <ProductUnitSelector
            product={purchaseItem.product}
            sellItem={purchaseItem}
            setSellItem={setPurchaseItem}
          />
        )}

        {/* Price */}
        <TextField
          label={t('common.cost')}
          type="text"
          inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
          value={purchaseItem?.price}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9.,]/g, "");
            setPurchaseItem({ ...purchaseItem, price: value });
          }}
          sx={{ marginTop: 1, "& .MuiInputBase-input": { textAlign: "right" } }}
          inputRef={refs.price}
          error={!!errors.price}
          helperText={errors.price}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              refs.discount.current.select();
              refs.discount.current.focus();
            }
          }}
          InputLabelProps={{ shrink: !!purchaseItem?.price }}
        />

        {/* Discount */}  
        <TextField
          label={t('common.discount')}
          type="text"
          inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
          value={purchaseItem?.discount_surcharge}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9.,]/g, "");
            setPurchaseItem({ ...purchaseItem, discount_surcharge: value });
          }}
          sx={{ marginTop: 1, "& .MuiInputBase-input": { textAlign: "right" } }}
          inputRef={refs.discount}
          error={!!errors.discount}
          helperText={errors.discount}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddProduct();
              refs.product.current.select();
              refs.product.current.focus();
            }
          }}
          InputLabelProps={{ shrink: !!purchaseItem?.discount_surcharge }}
        />

        <IconButton onClick={handleAddProduct} sx={{ marginTop: 1 }}>
          <KeyboardReturnIcon />
        </IconButton>
      </Box>

      {/* Product list */}  
      <Box>
        {purchase.items?.map((item, index) => (
          <ListItemProductRemove
            key={index}
            product={item.product.name}
            description={getDescription(item)}
            subtotal={formatCurrency(
              getSubtotal(item.quantity, item.price, item.discount_surcharge)
            )}
            onDelete={handleRemoveProduct}
            index={index}
            thumbUrl={item.product.thumb_url}
            avatarContent={<InventoryIcon sx={{ fontSize: 20 }} />}
          />
        ))}
      </Box>

      {/* Confirmation Dialog - original without payment */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('edit.confirmPurchaseNoPayments')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('edit.confirmPurchaseNoPaymentsDesc')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirmSave} color="primary" autoFocus disabled={isLoading}>
            {t('common.continue')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={handlePaymentDialogClose}
        aria-labelledby="payment-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="payment-dialog-title">
          {t('edit.howWillPayment')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText mb={2}>
            Total: {formatCurrency(purchase.total)}
            {(() => {
              const balanceInfo = setBalanceInViews();
              
              if (balanceInfo.isPerfect) return null;
              
              if (balanceInfo.isDebt) {
                return (
                  <span style={{ color: 'red', marginLeft: '10px' }}>
                    | {t('edit.debt')} {formatCurrency(Math.abs(balanceInfo.balance))}
                  </span>
                );
              } else if (balanceInfo.isChange) {
                return (
                  <span style={{ color: 'green', marginLeft: '10px' }}>
                    | {t('edit.change')} {formatCurrency(balanceInfo.balance)}
                  </span>
                );
              }
              return null;
            })()}
          </DialogContentText>
          
          {/* Payment input form */}
          <Box display="flex" flexDirection="column" gap={2} mb={2}>
            <TextField
              label={t('common.amount')}
              type="text"
              inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
              value={paymentItem?.amount}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9.,]/g, "");
                setPaymentItem({ ...paymentItem, amount: value });
              }}
              sx={{ "& .MuiInputBase-input": { textAlign: "center" } }}
              inputRef={refs.amount}
              error={!!errors.amount}
              helperText={errors.amount}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (refs.payment.current) {
                    refs.payment.current.focus();
                  }
                }
              }}
              fullWidth
            />
            
            <Autocomplete
              fullWidth
              options={paymentsTypes}
              getOptionLabel={(option) => (option ? option.name : "")}
              value={paymentItem?.finance_types_payment}
              disableClearable={true}
              onChange={(e, newValue) => {
                if (newValue) {
                  // Calculate remaining amount to pay
                  const totalExistingPayments = purchase.payments.reduce(
                    (sum, payment) => sum.plus(new Decimal(payment.amount.toString().replace(",", ".") || 0)),
                    new Decimal(0)
                  );
                  const currentTotal = new Decimal(purchase.total || 0);
                  const remainingAmount = currentTotal.minus(totalExistingPayments);

                  setPaymentItem({
                    ...paymentItem,
                    finance_types_payment: newValue,
                    amount: remainingAmount.greaterThan(0) ? remainingAmount.toString() : "",
                    date: dayjs().format('YYYY-MM-DD HH:mm:ss'),
                    id: newValue.id,
                    name: newValue.name,
                  });
                } else {
                  resetPaymentItem();
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('edit.paymentMethods')}
                  name="paymentType"
                  inputRef={refs.payment}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handlePaymentDialogSave();
                    }
                  }}
                />
              )}
            />

            <Box display="flex" justifyContent="flex-start">
              <Button 
                onClick={handleAddPayment} 
                variant="text" 
                color="primary"
                sx={{ textTransform: 'none' }}
              >
                {t('edit.addMultiplePayments')}
              </Button>
            </Box>
          </Box>

          {/* Payments list */}
          <List>
            {purchase.payments?.map((item, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() => handleRemovePayment(index)}
                    sx={{ width: "42px", height: "42px" }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                }
                sx={{ paddingRight: "56px" }}
              >
                <ListItemText
                  primary={`${dayjs(item.date).format(dateDisplayFormat)}. ${item.finance_types_payment.name}: ${formatCurrency(item.amount)}`}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePaymentDialogClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handlePaymentDialogSave} 
            color="primary" 
            autoFocus 
          >
            {t('common.continue')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Dialog */}
      <Dialog
        open={reviewDialogOpen}
        onClose={handleReviewDialogClose}
        aria-labelledby="review-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="review-dialog-title">
          {t('edit.reviewTitlePurchase')}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} mt={0}>
            
            {/* Provider Info */}
            <Box>
              {purchase.provider && (
                <ListItemWAvatarRemove
                  primary={purchase.provider.name}
                  avatarContent={<LocalShipping />}
                />
              )}
            </Box>

            {/* Deposit */}
            {deposits.length > 1 && purchase.deposit && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                    {t('edit.warehouse')} {purchase.deposit.name}
                </Typography>
              </Box>
            )}

            {/* Receipt */}
            {purchase.receipt && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('edit.purchaseReceipt')} {purchase.receipt}
                </Typography>
              </Box>
            )}

            {/* Products Details */}
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('edit.purchaseProducts')}
              </Typography>
              <List dense>
                {purchase.items?.map((item, index) => (
                  <ListItemProductRemove
                    key={index}
                    product={item.product.name}
                    description={getDescription(item)}
                    subtotal={formatCurrency(
                      getSubtotal(item.quantity, item.price, item.discount_surcharge)
                    )}
                    thumbUrl={item.product.thumb_url}
                    avatarContent={<InventoryIcon sx={{ fontSize: 20 }} />}
                  />
                ))}
              </List>
            </Box>

            {/* Total and Balance */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Total: {formatCurrency(purchase.total)}
              </Typography>
              {(() => {
                const balance = calculateBalance();
                const balanceValue = balance.toNumber();
                
                if (balanceValue === 0) {
                  return (
                    <Typography variant="body1" color="success.main">
                      {t('edit.fullPayment')}
                    </Typography>
                  );
                } else if (balanceValue > 0) {
                  return (
                    <Typography variant="body1" color="error.main">
                      {t('edit.debt')} {formatCurrency(balanceValue)}
                    </Typography>
                  );
                } else {
                  return (
                    <Typography variant="body1" color="success.main">
                      {t('edit.change')} {formatCurrency(Math.abs(balanceValue))}
                    </Typography>
                  );
                }
              })()}
            </Box>

            {/* Payments */}
            {purchase.payments && purchase.payments.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('common.paymentMethod')}
                </Typography>
                <List dense>
                  {purchase.payments.map((payment, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <ListItemText
                        primary={`${payment.finance_types_payment.name}: ${formatCurrency(payment.amount)}`}
                        secondary={dayjs(payment.date).format(dateDisplayFormat)}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReviewDialogClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleReviewDialogSave} 
            color="primary" 
            variant="contained"
            autoFocus 
            disabled={isLoading}
          >
            {t('edit.finalize')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={successDialogOpen}
        onClose={handleSuccessDialogClose}
        aria-labelledby="success-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="success-dialog-title" sx={{ textAlign: 'center' }}>
          {t('edit.purchaseSuccess')}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <Button
              variant="outlined"
              onClick={handleSuccessDialogClose}
              fullWidth
              size="large"
            >
              {t('common.close')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

    </EditPage>
  );
};

export default EditPurchase;
